// Chat Hour: two 60-minute windows a day when people come to Zuzu at the same
// time, so venters and listeners find each other faster. Fixed in UTC, so it is
// the same moment everywhere (2 AM and 2 PM in New York in summer, 1 AM and 1 PM
// in winter; 7 AM and 7 PM in London in summer). Shared with the server.

export const CHAT_HOUR_PATH = '/chat-hour';
export const CHAT_HOUR_UTC = [6, 18]; // start hours, UTC
export const CHAT_HOUR_MINUTES = 60;
export const REMIND_LEADS = [10, 0] as const; // minutes before the start

export type Session = { start: Date; end: Date; slot: number };

// Sessions around now (yesterday to the day after tomorrow), oldest first.
export function chatHourSessions(now = new Date()): Session[] {
  const out: Session[] = [];
  for (let day = -1; day <= 2; day++) {
    CHAT_HOUR_UTC.forEach((h, slot) => {
      const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + day, h));
      out.push({ start, end: new Date(+start + CHAT_HOUR_MINUTES * 60_000), slot });
    });
  }
  return out.sort((a, b) => +a.start - +b.start);
}
export const liveSession = (now = new Date()) => chatHourSessions(now).find(s => s.start <= now && now < s.end);
export const nextSession = (now = new Date()) => chatHourSessions(now).find(s => s.start > now)!;

// Places shown on the page and the map. One per country, except big countries
// with several time zones (shown with the zone's short name, like EDT).
export type ChatZone = { tz: string; country: string; flag: string; city?: string; multi?: boolean; locale?: string; lat: number; lon: number; main?: boolean };
export const CHAT_ZONES: ChatZone[] = [
  { tz: 'America/Los_Angeles', country: 'United States', flag: '🇺🇸', city: 'Los Angeles', multi: true, lat: 34, lon: -118, main: true },
  { tz: 'America/Chicago', country: 'United States', flag: '🇺🇸', city: 'Chicago', multi: true, lat: 42, lon: -88 },
  { tz: 'America/New_York', country: 'United States', flag: '🇺🇸', city: 'New York', multi: true, lat: 41, lon: -74, main: true },
  { tz: 'America/Toronto', country: 'Canada', flag: '🇨🇦', city: 'Toronto', multi: true, lat: 44, lon: -79 },
  { tz: 'America/Mexico_City', country: 'Mexico', flag: '🇲🇽', lat: 19, lon: -99 },
  { tz: 'America/Sao_Paulo', country: 'Brazil', flag: '🇧🇷', lat: -23, lon: -47, main: true },
  { tz: 'Europe/London', country: 'United Kingdom', flag: '🇬🇧', lat: 51.5, lon: -0.1, main: true },
  { tz: 'Europe/Berlin', country: 'Germany', flag: '🇩🇪', lat: 52.5, lon: 13.4, main: true },
  { tz: 'Africa/Lagos', country: 'Nigeria', flag: '🇳🇬', lat: 6.5, lon: 3.4, main: true },
  { tz: 'Africa/Nairobi', country: 'Kenya', flag: '🇰🇪', lat: -1.3, lon: 36.8 },
  { tz: 'Africa/Johannesburg', country: 'South Africa', flag: '🇿🇦', lat: -26, lon: 28 },
  { tz: 'Asia/Dubai', country: 'UAE', flag: '🇦🇪', lat: 25.2, lon: 55.3, main: true },
  { tz: 'Asia/Karachi', country: 'Pakistan', flag: '🇵🇰', lat: 24.9, lon: 67 },
  { tz: 'Asia/Kolkata', country: 'India', flag: '🇮🇳', lat: 22, lon: 78, main: true },
  { tz: 'Asia/Dhaka', country: 'Bangladesh', flag: '🇧🇩', lat: 23.8, lon: 90.4 },
  { tz: 'Asia/Jakarta', country: 'Indonesia', flag: '🇮🇩', city: 'Jakarta', multi: true, locale: 'id-ID', lat: -6.2, lon: 106.8 },
  { tz: 'Asia/Manila', country: 'Philippines', flag: '🇵🇭', lat: 14.6, lon: 121, main: true },
  { tz: 'Asia/Singapore', country: 'Singapore', flag: '🇸🇬', lat: 1.35, lon: 103.8 },
  { tz: 'Asia/Tokyo', country: 'Japan', flag: '🇯🇵', lat: 35.7, lon: 139.7, main: true },
  { tz: 'Australia/Sydney', country: 'Australia', flag: '🇦🇺', city: 'Sydney', multi: true, locale: 'en-AU', lat: -33.9, lon: 151.2, main: true },
  { tz: 'Australia/Perth', country: 'Australia', flag: '🇦🇺', city: 'Perth', multi: true, locale: 'en-AU', lat: -31.9, lon: 115.9 },
  { tz: 'Pacific/Auckland', country: 'New Zealand', flag: '🇳🇿', lat: -36.8, lon: 174.8 },
];
// Older names some devices still report.
const TZ_ALIASES: Record<string, string> = {
  'Asia/Calcutta': 'Asia/Kolkata', 'US/Pacific': 'America/Los_Angeles', 'US/Eastern': 'America/New_York', 'US/Central': 'America/Chicago',
  'Asia/Katmandu': 'Asia/Kathmandu', 'Asia/Saigon': 'Asia/Ho_Chi_Minh', 'Asia/Rangoon': 'Asia/Yangon', 'Europe/Kiev': 'Europe/Kyiv',
};
export const canonicalTz = (tz: string) => TZ_ALIASES[tz] ?? tz;

// The short zone name, like EDT or AEST (only for countries with several zones).
export function zoneShort(z: ChatZone, at = new Date()) {
  try {
    return new Intl.DateTimeFormat(z.locale ?? 'en-US', { timeZone: z.tz, timeZoneName: 'short' })
      .formatToParts(at).find(p => p.type === 'timeZoneName')?.value ?? '';
  } catch { return ''; }
}
export const zoneLabel = (z: ChatZone, at = new Date()) => (z.multi ? `${z.country} · ${zoneShort(z, at)}` : z.country);

export const timeIn = (d: Date, tz: string) => new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz }).format(d);
export const hourIn = (d: Date, tz: string) => Number(new Intl.DateTimeFormat('en-GB', { hour: '2-digit', hourCycle: 'h23', timeZone: tz }).format(d));
export const isDaytime = (d: Date, tz: string) => { const h = hourIn(d, tz); return h >= 6 && h < 18; };
// How a time feels for someone: fits the day, late night, or early.
export const fitFor = (d: Date, tz: string) => { const h = hourIn(d, tz); return h >= 8 && h < 23 ? 'Good for you' : (h >= 23 || h < 2) ? 'Late night for you' : 'Early for you'; };

// "Add to calendar": a daily event for one slot (Google), or both (.ics file).
const stamp = (d: Date) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
const CAL_TEXT = 'Zuzu’s Chat Hour ⏳💬';
const CAL_DETAILS = 'Zuzu’s Chat Hour: 60 minutes when people come together to talk and listen, anonymously. Open https://www.zuzu.club/chat-hour';
export function googleCalendarUrl(s: Session) {
  const q = new URLSearchParams({
    action: 'TEMPLATE', text: CAL_TEXT, details: CAL_DETAILS, location: 'https://www.zuzu.club',
    dates: `${stamp(s.start)}/${stamp(s.end)}`, recur: 'RRULE:FREQ=DAILY',
  });
  return `https://calendar.google.com/calendar/render?${q.toString()}`;
}
export function chatHourIcs(sessions: Session[]) {
  const lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Zuzu Club//Chat Hour//EN', 'CALSCALE:GREGORIAN'];
  for (const s of sessions) {
    lines.push('BEGIN:VEVENT', `UID:chat-hour-${s.slot}@zuzu.club`, `DTSTAMP:${stamp(new Date())}`, `DTSTART:${stamp(s.start)}`, `DTEND:${stamp(s.end)}`,
      'RRULE:FREQ=DAILY', `SUMMARY:${CAL_TEXT}`, `DESCRIPTION:${CAL_DETAILS}`, 'URL:https://www.zuzu.club/chat-hour',
      'BEGIN:VALARM', 'TRIGGER:-PT10M', 'ACTION:DISPLAY', `DESCRIPTION:${CAL_TEXT} starts in 10 minutes`, 'END:VALARM', 'END:VEVENT');
  }
  lines.push('END:VCALENDAR');
  return lines.join('\r\n');
}

export const CHAT_HOUR_META = {
  title: 'Chat Hour ⏳💬 | Twice a Day, Talk or Listen With People | Zuzu Club',
  description: 'Twice a day, people come to Zuzu at the same time to talk and listen, anonymously. See Chat Hour in your time, set a reminder or add it to your calendar.',
  image: 'https://www.zuzu.club/og/zuzu-club-chat-hour.jpg?v=10',
  alt: 'Zuzu’s Chat Hour: twice a day, the lights come on. Come talk, or come listen.',
  keywords: 'chat hour, talk to someone tonight, people online to talk, anonymous chat time, vent to a stranger, listen to someone, zuzu chat hour',
};

export const CHAT_HOUR_COPY = {
  name: 'Zuzu’s Chat Hour ⏳💬',
  eyebrow: 'Zuzu’s Chat Hour ⏳💬',
  heading: 'Twice a day, the lights come on.',
  headingEm: 'Come talk, or come listen.',
  lead: 'Zuzu’s Chat Hour is 60 minutes, twice a day, when people from around the world come to Zuzu at the same time. More people here means you find someone to talk to in seconds.',
  note: [
    'I was looking for a way to help more conversations happen on Zuzu. So I chose one common time in the day and one at night, when people can come together and connect.',
    'I know these hours won’t suit everyone, and for some of you they will land at odd times. As Zuzu grows, I will keep improving this and make it work for more places.',
  ],
  noteCta: 'Have an idea? Share it with me',
};

// Meet Again, in short: Chat Hour is the easiest time to find someone again.
export const CHAT_HOUR_MEET_AGAIN = {
  eyebrow: 'Meet Again',
  heading: 'Want to talk to someone again?',
  text: 'Had a good chat? Both of you tap Meet again at the top of the chat. If you both tap, Zuzu lets you know when you are both here, and you can talk again. If only you tap, nobody knows. Chat Hour is the easiest time to find each other.',
  cta: 'See how Meet Again works',
  href: '/meet-again',
};
